import * as Yup from "yup";
import { PAYMENT_MODES, PAYMENT_TYPES } from "./types";

interface Payment {
  amount: string;
  isDeleted?: boolean;
}

export const otherExpenseValidationSchema = Yup.object().shape({
  expenseDate: Yup.string()
    .required("Expense date is required")
    .matches(
      /^\d{4}-\d{2}-\d{2}$/,
      "Please enter a valid date in YYYY-MM-DD format"
    ),
  category: Yup.string()
    .required("Category is required")
    .min(2, "Category must be at least 2 characters"),
  paidTo: Yup.string()
    .required("Paid to is required")
    .min(2, "Paid to must be at least 2 characters")
    .max(100, "Paid to must not exceed 100 characters"),
  amount: Yup.number()
    .required("Amount is required")
    .positive("Amount must be a positive number")
    .min(1, "Amount must be at least ₹1")
    .max(10000000, "Amount must not exceed ₹1,00,00,000"),
  contactNumber: Yup.string()
    .matches(/^[+]?[\d\s\-\(\)]{10,15}$/, "Please enter a valid contact number")
    .nullable()
    .notRequired(),
  location: Yup.string()
    .max(200, "Location must not exceed 200 characters")
    .nullable()
    .notRequired(),
  notes: Yup.string()
    .max(500, "Notes must not exceed 500 characters")
    .nullable()
    .notRequired(),
});

const paymentDetailSchema = Yup.object().shape({
  type: Yup.string()
    .required("Payment type is required")
    .oneOf(PAYMENT_TYPES, "Invalid payment type"),
  mode: Yup.string()
    .required("Payment mode is required")
    .oneOf(PAYMENT_MODES, "Invalid payment mode"),
  amount: Yup.string()
    .required("Payment amount is required")
    .matches(/^[0-9]*\.?[0-9]+$/, "Amount must be a valid decimal number")
    .test("min", "Amount must be greater than 0", (value) => Number(value) > 0),
  date: Yup.string().required("Date is required"),
  location: Yup.string(),
  receivedFrom: Yup.string(),
  remarks: Yup.string(),
  status: Yup.string(),
  isEdited: Yup.boolean(),
  isDeleted: Yup.boolean(),
});

export const multipleExpenseValidationSchema = Yup.object().shape({
  expenses: Yup.array().of(
    Yup.object().shape({
      expenseType: Yup.string().required("Expense type is required"),
      category: Yup.string().required("Category is required"),
      expenseDate: Yup.string().required("Date is required"),
      paidTo: Yup.string().required("Paid to is required"),
      amount: Yup.number()
        .required("Amount is required")
        .min(0, "Amount must be greater than or equal to 0"),
      contactNumber: Yup.string(),
      location: Yup.string(),
      notes: Yup.string(),
      payments: Yup.array()
        .of(paymentDetailSchema)
        .test(
          "total-not-exceeding",
          "Total payments cannot exceed expense amount",
          function (payments: Payment[] = []) {
            const expense = this.parent;
            const totalPaid = payments
              .filter((p) => !p.isDeleted)
              .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
            return totalPaid <= Number(expense.amount);
          }
        ),
    })
  ),
});

export const otherExpenseFiltersValidationSchema = Yup.object().shape({
  category: Yup.string().notRequired(),
  paidTo: Yup.string().notRequired(),
  startDate: Yup.string()
    .matches(
      /^\d{4}-\d{2}-\d{2}$/,
      "Please enter a valid start date in YYYY-MM-DD format"
    )
    .notRequired(),
  endDate: Yup.string()
    .matches(
      /^\d{4}-\d{2}-\d{2}$/,
      "Please enter a valid end date in YYYY-MM-DD format"
    )
    .notRequired(),
  sortBy: Yup.string()
    .oneOf(["expenseDate", "category", "amount", "createdAt"])
    .notRequired(),
  sortOrder: Yup.string().oneOf(["ASC", "DESC"]).notRequired(),
});
